import { Box, Button, Stack } from "@mui/material";
import { EmployeesTable } from "Components/Merchant/Employees/EmployeesTable/EmployeesTable";
import { ErrorText } from "Components/Shared/Errors/ErrorText";
import { PlusMathIcon } from "Components/Shared/Icons";
import {
  useMerchantEmployees,
  type EmployeesListParams,
} from "Api/Queries/Merchants/Employees/useMerchantEmployees";
import { useServerTable } from "Hooks/useServerTable";
import type { EmployeeDto } from "Infrastructure/Api/Api";
import { Link } from "Navigation/Link";
import { useMemo } from "react";
import { appUrl } from "Utils/UrlUtils";
import { Resources, useResource } from "Translations/Resources";
import { asString, asStringArray } from "Utils/FilterUtils";

// Keep in sync with EmployeesFilterRequest.AllowedSortColumns on BE.
const SORT_COLUMN_MAP: Record<string, string> = {
  firstName: "Party.FirstName",
  lastName: "Party.LastName",
  email: "Party.Email",
  status: "Status",
};

const COMPONENT_RESOURCES = Resources.Employee.List;

export const EmployeesPageContent = () => {
  const { t } = useResource();

  const { queryParams, tableProps } = useServerTable<EmployeeDto>({
    defaultSorting: [{ id: "lastName", desc: false }],
    defaultPageSize: 15,
  });

  const apiParams = useMemo(
    (): EmployeesListParams => ({
      offset: queryParams.offset,
      limit: queryParams.limit,
      sortBy: queryParams.sortBy
        ? (SORT_COLUMN_MAP[queryParams.sortBy] ?? queryParams.sortBy)
        : undefined,
      sortDirection: queryParams.sortDirection,
      statuses: asStringArray(queryParams.filters.status),
      firstName: asString(queryParams.filters.firstName),
      lastName: asString(queryParams.filters.lastName),
      email: asString(queryParams.filters.email),
      phone: asString(queryParams.filters.phone),
    }),
    [queryParams],
  );

  const {
    data: employees,
    isFetching,
    isError,
  } = useMerchantEmployees(undefined, apiParams);

  if (isError) {
    return <ErrorText>{t(COMPONENT_RESOURCES.LoadingError)}</ErrorText>;
  }

  return (
    <Stack className="left-content" gap={2}>
      <Box display="flex" justifyContent="flex-start">
        <Button
          component={Link}
          type="button"
          color="primary"
          variant="text"
          to={appUrl("create-employee")}
          startIcon={<PlusMathIcon width={20} height={20} />}
        >
          {t(COMPONENT_RESOURCES.AddEmployee)}
        </Button>
      </Box>
      <Box display="grid">
        <EmployeesTable
          data={employees?.items || []}
          isLoading={isFetching}
          rowCount={employees?.totalCount ?? 0}
          tableProps={tableProps}
        />
      </Box>
    </Stack>
  );
};
